import * as React from "react"
import {useIntl, FormattedMessage, FormattedNumber} from "react-intl"
import styled from "styled-components"
import {layout, typography} from "styled-system"

import numeral from "numeral"
import "numeral/locales"

// import isNaN from "lodash/isNaN"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import {faExclamationTriangle} from "@fortawesome/pro-regular-svg-icons/faExclamationTriangle"

import {useId} from "@reach/auto-id"
import {Dialog} from "@reach/dialog"

import NotificationsContext from "../../../contexts/notifications.js"

import useDebounce from "../../../hooks/debounce.js"
import useFetcher from "../../../hooks/fetcher.js"

import {
  Column as DialogColumn,
  Content as DialogContent,
} from "../../../components/dialog.js"
import Flex from "../../../components/flex.js"
import Loader from "../../../components/loader.js"
import Row from "../../../components/row.js"
import Text from "../../../components/text.js"
import View from "../../../components/view.js"
import {StyledButton, StyledInput} from "../../../components/styled.js"

const StyledPriceInput = styled(StyledInput).attrs({
  textAlign: "left",
  width: "price-input-lg",
  height: "38px",
  lineHeight: "38px",
  px: 1,
  mr: 1,
})`
  ${layout} ${typography}
`

const CancelButton = styled(StyledButton).attrs({backgroundColor: "warning"})``

const DebitButton = props => {
  const {id, clientID, currency, bankAuthValue, onWithdraw} = props

  const intl = useIntl()
  const {addError, addSuccess} = React.useContext(NotificationsContext)

  const [priceRef, setPriceRef] = React.useState()
  const [price, setPrice] = React.useState(bankAuthValue)
  const [debouncedPrice] = useDebounce(price, 500)

  const [previewWithdrawURL, setPreviewWithdrawURL] = React.useState(null)
  const previewWithdrawData = useFetcher(previewWithdrawURL, {nocache: true})
  const [withdrawURL, setWithdrawURL] = React.useState(null)
  const withdrawData = useFetcher(withdrawURL, {nocache: true})

  const [askConfirm, setAskConfirm] = React.useState(false)

  const [showDialog, setShowDialog] = React.useState(false)
  const open = () => setShowDialog(true)
  const close = () => {
    if (askConfirm) setAskConfirm(false)
    setShowDialog(false)
  }

  const reachID = `debit-button--${useId(id)}`

  const formatPrice = price =>
    intl
      .formatNumber(price, {
        style: "currency",
        currency: currency.code,
        currencyDisplay: "code",
      })
      .replace(/[a-z]{3}/i, "")
      .trimEnd()

  React.useEffect(() => {
    if (!intl?.locale) return
    numeral.locale(intl.locale === "en" ? "en-gb" : intl.locale)
  }, [intl.locale])

  React.useEffect(() => {
    if (!showDialog) return
    if (!priceRef) return
    if (price === null || price === undefined) return
    priceRef.value = formatPrice(price)
  }, [showDialog, priceRef, price])

  React.useEffect(() => {
    setPreviewWithdrawURL(
      `home/clients/${clientID}/arrivals/withdraw?id=${id}&amount=${price}&perform=0`
    )
  }, [debouncedPrice])

  React.useEffect(() => {
    if (!previewWithdrawData?.data) return
  }, [previewWithdrawData])

  React.useEffect(() => {
    if (withdrawData?.error) {
      addError(<FormattedMessage id="debit-button.notification.error" />)
    }
    if (!withdrawData?.data?.performed) return
    addSuccess(
      <FormattedMessage
        id="debit-button.notification.success"
        values={{
          amount: (
            <FormattedNumber
              style="currency"
              currency={currency.code}
              value={withdrawData.data.effectiveAmountWithdrawn}
            />
          ),
        }}
      />
    )
    onWithdraw()
    close()
  }, [withdrawData])

  const handlePriceBlur = event => {
    setPrice(numeral(event.target.value).value())
  }

  const handleWithdraw = () => {
    setWithdrawURL(
      `home/clients/${clientID}/arrivals/withdraw?id=${id}&amount=${price}&perform=1`
    )
  }

  return (
    <>
      <StyledButton onClick={open}>
        <FormattedMessage id="debit-button.button" />
      </StyledButton>
      {showDialog && (
        <Dialog onDismiss={close} aria-labelledby={reachID}>
          {askConfirm ? (
            <DialogContent
              title={<FormattedMessage id="debit-button.alert" />}
              close={close}
              buttons={
                <>
                  <StyledButton onClick={handleWithdraw}>OK</StyledButton>
                  <CancelButton onClick={() => setAskConfirm(false)} ml={3}>
                    <FormattedMessage id="alert.cancel" />
                  </CancelButton>
                </>
              }
            ></DialogContent>
          ) : (
            <DialogContent
              title={<FormattedMessage id="debit-button.title" />}
              close={close}
              buttons={
                <StyledButton onClick={() => setAskConfirm(true)}>
                  {previewWithdrawData?.loading || withdrawData?.loading ? (
                    <Loader size="2x" style={{verticalAlign: "middle"}} />
                  ) : (
                    <FormattedMessage id="debit-button.confirm" />
                  )}
                </StyledButton>
              }
            >
              <Row>
                <View>
                  <DialogColumn
                    label={<FormattedMessage id="debit-button.to_debit" />}
                  >
                    <StyledPriceInput
                      ref={setPriceRef}
                      onBlur={handlePriceBlur}
                    />
                    <Text>{currency.symbol}</Text>
                  </DialogColumn>
                </View>
                <View>
                  <DialogColumn
                    label={<FormattedMessage id="debit-button.bank_auth" />}
                  >
                    <StyledPriceInput
                      disabled
                      defaultValue={intl
                        .formatNumber(bankAuthValue, {
                          style: "currency",
                          currency: currency.code,
                          currencyDisplay: "code",
                        })
                        .replace(/[a-z]{3}/i, "")
                        .trimEnd()}
                    />
                    <Text>{currency.symbol}</Text>
                  </DialogColumn>
                </View>
              </Row>
              {/* {!isNaN(parseFloat(price)) &&
          parseFloat(price) > parseFloat(bankAuthValue) ? ( */}
              {previewWithdrawData?.data?.performed === false ? (
                <Row mt={4} alignItems="flex-start">
                  <Flex nogrow noshrink pt={1}>
                    <FontAwesomeIcon
                      icon={faExclamationTriangle}
                      size="lg"
                      onClick={close}
                      color="#ffa500"
                      style={{cursor: "pointer"}}
                    />
                  </Flex>
                  <View ml={2}>
                    {previewWithdrawData?.data?.operations?.length > 1 ? (
                      <Row>
                        <Text color="warning">
                          <FormattedMessage id="debit-button.warning" />
                        </Text>
                      </Row>
                    ) : null}
                    {previewWithdrawData?.data?.operations.map(
                      (operation, index) => (
                        <Row key={index}>
                          <Text color="warning">
                            <FormattedMessage
                              id={`debit-button.operation.${operation.type}`}
                              values={{
                                amount: (
                                  <FormattedNumber
                                    style="currency"
                                    currency={currency.code}
                                    value={operation?.amount}
                                  />
                                ),
                              }}
                            />
                          </Text>
                        </Row>
                      )
                    )}
                  </View>
                </Row>
              ) : null}
            </DialogContent>
          )}
        </Dialog>
      )}
    </>
  )
}

export default DebitButton
